import React from 'react';
import classNames from 'classnames';
import {
  EOrganization,
  ERate,
  ESnapshot,
  ESnapshotExists,
  ETemplate
} from 'lib/types';
import { DropdownButton } from 'components/DropdownButton';
import { getBooleanFlag } from 'utils/flags';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import { useAppSelector } from 'redux/hooks';
import PlaceNoticePreview from './PlaceNoticePreview';
import { useUpdatePreview } from './hooks/useUpdatePreview';
import { DeliveryPricingSummary } from './DeliveryAddOns/DeliveryPricingSummary';
import { hasPopulatedAddress } from './NoticeDelivery/deliveryRecipients.helpers';

type PlacementSidebarProps = {
  showContent: boolean;
  setShowContent: (show: boolean) => void;
  rate?: ESnapshot<ERate>;
  adTemplate: ESnapshotExists<ETemplate> | null;
  newspaper: ESnapshotExists<EOrganization> | undefined;
  uploading: boolean;
};

export default function PlacementSidebar({
  showContent,
  setShowContent,
  rate,
  adTemplate,
  newspaper,
  uploading
}: PlacementSidebarProps) {
  const id = 'preview-sidebar';
  const updatingPreview = useUpdatePreview({ newspaper, adTemplate });

  const deliveryRecipients = useAppSelector(state => state.deliveryRecipients);

  const addOnsPricingConfig = newspaper?.data().noticeAddOnPricing ?? {};
  const addOnsEnabled = getBooleanFlag(
    LaunchDarklyFlags.ENABLE_AFFIDAVIT_INVOICE_ADDONS,
    false
  );

  // Blank placeholder rows carry copies: 1 and would otherwise be priced as a
  // standard-affidavit mailing, so only populated rows reach the pricing engine.
  const mailRecipients = [
    ...deliveryRecipients.mailingRecipients,
    ...deliveryRecipients.invoiceMailingRecipients
  ].filter(hasPopulatedAddress);

  const showAddOnsSummary = addOnsEnabled && mailRecipients.length > 0;

  return (
    <div
      id={id}
      className={classNames('hidden md:block fixed h-full top-0 right-0', {
        'md:w-1/3': showContent,
        'w-12': !showContent
      })}
    >
      <div style={{ top: '112px', left: '-15px' }} className="absolute">
        <DropdownButton
          id={`${id}-dropdown`}
          textLabel={`${id}-dropdown`}
          size="sm"
          startingDirection={'left'}
          overrideExpand={showContent}
          onClick={() => setShowContent(!showContent)}
        />
      </div>
      <div
        style={{ paddingTop: '88px' }}
        className="mt-6 w-full h-full border-l bg-column-gray-25"
      >
        {showContent && (
          <div className="overflow-y-auto h-full pb-12">
            <PlaceNoticePreview
              rate={rate}
              newspaper={newspaper}
              uploading={uploading}
              loading={updatingPreview}
            />
            {showAddOnsSummary && (
              <div className="px-4 mt-6">
                <DeliveryPricingSummary
                  mailRecipients={mailRecipients}
                  config={addOnsPricingConfig}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
